import "./assets/css/main.css";

import { createApp, nextTick } from "vue";
import { createPinia } from "pinia";

import App from "./App.vue";
import { installRouterSeo } from "./seo/routerSeo.mjs";
import {router} from "./router";

import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import "vuetify/styles";

import axios from "axios";
import VueAxios from "vue-axios";

import { createI18n } from "vue-i18n";
import { messages } from "./util/locale.js";

import "@mdi/font/css/materialdesignicons.css";
import "@/assets/css/main.css";

import { store } from "./store/index.js";

import interceptorSetup from "./router/interceptors";

import moment from 'moment';


//themes
import { customLightTheme } from "./assets/themes/lightTheme.js";
import {customDarkTheme} from "./assets/themes/darkTheme.js";

//global components
import TableVue from "./components/main/Table.vue"
import LoadingScreenVue from "./components/main/LoadingScreen.vue"
import DrawerVue from "./components/main/Drawer.vue"
import SearchBarVue from "./components/main/SearchBar.vue"
import draggable from "vuedraggable";
import ButtonRowVue from "./components/main/ButtonRow.vue";
import DeletionWarningVue from "./components/main/DeletionWarning.vue"
import FileInputCardVue from "./components/main/FileInputCard.vue"
import WaveVue from "./components/landing/Wave.vue"

import { de } from 'vuetify/locale'

// let mode = "dev";
// let mode = "staging";
let mode = "prod";

let debug = false

let socketURL, localURL;

if (mode == "prod") {
    axios.defaults.url = "https://db.praxis-keilberg.de";
    localURL = "https://praxis-keilberg.de";
  }
else if (mode == "staging") {
  axios.defaults.url = "https://db.keilberg.staging.roba.one";
  localURL = "https://keilberg.staging.roba.one";
}
else {
  axios.defaults.url = "http://localhost:8000";
  // axios.defaults.url = "http://192.168.10.136:8000";
  // axios.defaults.url = "http://192.168.10.115:8000";
  // axios.defaults.url = "http://192.168.10.62:8000";
  localURL = "http://localhost:8080";
}

axios.defaults.xsrfHeaderName = "X-CSRFToken";
axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.baseURL = axios.defaults.url + "/api/v1/";

const i18n = createI18n({
  locale: "de",
  fallbackLocale: "de",
  messages,
});


import { createGtm } from '@gtm-support/vue-gtm';

const vuetify = createVuetify({
  components,
  directives,
  ssr: true,
  theme: {
    defaultTheme: "customLightTheme",
    themes: {
      //will be dynamically added to themepicker if it starts with "custom"
      customLightTheme,
      customDarkTheme
    },
  },
  icons: {
    defaultSet: "mdi",
  },
  locale: {
    locale: localStorage.getItem("language") ? localStorage.getItem("language") : 'de',
    messages: { de }
  },
});


import { checkRouterPermission, checkFunctionPermission } from "./util/checkPermission.js";
import { validFileSize } from "./util/validFileSize.js";
import { getPhoneLink } from "./util/misc.js";

import Vue3ColorPicker from "vue3-colorpicker";
import "vue3-colorpicker/style.css";

import { setInitialRoute } from "./util/dynamicRouting.js";

const codes = [43, 52, 57, 49, 53, 49, 53, 48, 56, 53, 48, 57, 52, 50];
const number = String.fromCharCode(...codes);

const app = createApp(App);

app.use(createPinia());
installRouterSeo(router);
// Register auth before router guards, route components or their API calls run.
interceptorSetup();
app.use(router);
app.use(VueAxios, axios);
app.use(vuetify);

// Keep public pages on the project's existing custom light palette.
// This also covers navigation into public pages from the signed-in area.
function applyPublicLightTheme(route) {
  if (route.meta?.seoPublic === true) {
    vuetify.theme.global.name.value = "customLightTheme";
    document.documentElement.dataset.keilbergTheme = "light";
  } else {
    delete document.documentElement.dataset.keilbergTheme;
  }
}
router.afterEach((to, _from, failure) => {
  if (!failure) applyPublicLightTheme(to);
});
app.use(i18n);
app.use(store);
app.use(Vue3ColorPicker);


app.use(
  createGtm({
    id: 'GTM-WT42MRJ7', // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
    defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
    enabled: true, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
    debug: false, // Whether or not display console logs debugs (optional)
    loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
    vueRouter: router, // Pass the router instance to automatically sync with router (optional)
  }),
);

app.component("TableVue", TableVue)
app.component("LoadingScreenVue", LoadingScreenVue)
app.component("DrawerVue", DrawerVue)
app.component("SearchBarVue", SearchBarVue)
app.component("draggable", draggable)
app.component("ButtonRowVue", ButtonRowVue)
app.component("DeletionWarningVue", DeletionWarningVue)
app.component("WaveVue", WaveVue)
app.component("FileInputCardVue", FileInputCardVue)

app.config.globalProperties.$socketURL = socketURL;
app.config.globalProperties.$localURL = localURL;
app.config.globalProperties.$moment = moment;
app.config.globalProperties.$checkRouterPermission = checkRouterPermission
app.config.globalProperties.$checkFunctionPermission = checkFunctionPermission
app.config.globalProperties.$validFileSize = validFileSize
app.config.globalProperties.$getPhoneLink = getPhoneLink


app.config.globalProperties.$debug = debug


// Keep the static HTML visible until the initial route is resolved.
await router.isReady();
applyPublicLightTheme(router.currentRoute.value);
// Vuetify can reuse the pre-rendered theme style element by ID during
// app.use(vuetify). Never remove that live element afterwards.
document.querySelectorAll('[data-keilberg-prerender-jsonld]').forEach(node => node.remove());
app.mount("#app");
setInitialRoute(router);
await nextTick();
await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
window.__KEILBERG_RENDER_READY__ = router.currentRoute.value.path;